/**
 * Connection self-test shared between the UI runner and the selftest endpoints.
 * Results travel inside the Support Log export, so support gets both ends of the path in one tarball.
 */

/** Bounds the server enforces on the long probes; the client uses the defaults. */
export const SELFTEST_LIMITS = {
  STREAM_DEFAULT_SECONDS: 30,
  STREAM_MAX_SECONDS: 180,
  STREAM_CHUNK_INTERVAL_MS: 1000,
  BLOB_DEFAULT_BYTES: 1024 * 1024,
  BLOB_MAX_BYTES: 4 * 1024 * 1024,
} as const;

/** Name of the Server-Timing entry every response carries: time spent inside the backend process. */
export const SERVER_TIMING_METRIC = 'app';

/** Request headers worth echoing back: they reveal intermediaries and address rewriting. */
export const SELFTEST_ECHOED_HEADERS = [
  'host', 'via', 'forwarded', 'x-forwarded-for', 'x-forwarded-proto', 'x-forwarded-host', 'x-forwarded-port', 'x-real-ip',
] as const;

/** What the server saw of one request; the client compares it with its own view. */
export class SelftestEcho {
  /** server clock when the request arrived, epoch ms */
  receivedAt: number;
  processingMs: number;
  httpVersion: string;
  protocol: string;
  secure: boolean;
  hostname: string;
  ip: string;
  ips: string[];
  headers: Partial<Record<typeof SELFTEST_ECHOED_HEADERS[number], string>>;
  /** names only, values never leave the server */
  cookieNames: string[];
  workerIndex: number;
}

/** One line of the stream probe, newline-delimited JSON. */
export class SelftestStreamChunk {
  seq: number;
  sentAt: number;
}

export class SelftestUploadResult {
  bytes: number;
  ms: number;
}

export class TimingSample {
  clientMs: number;
  serverMs: number;
}

/** Subset of PerformanceResourceTiming, rounded, relative to the page's time origin. */
export class ResourceSample {
  name: string;
  initiatorType: string;
  nextHopProtocol: string;
  startTime: number;
  fetchStart: number;
  domainLookupStart: number;
  connectStart: number;
  secureConnectionStart: number;
  connectEnd: number;
  requestStart: number;
  responseStart: number;
  responseEnd: number;
  duration: number;
  transferSize: number;
  encodedBodySize: number;
  /** backend processing time from the Server-Timing header, absent for cached or foreign responses */
  serverMs?: number;
}

export type SelftestTransport = 'websocket' | 'polling';

export class TransportResult {
  connected: boolean;
  ms: number;
  error?: string;
}

export class StreamResult {
  seconds: number;
  chunks: number;
  /** client-side arrival gaps between consecutive chunks */
  gapsMs: number[];
  /** chunks arrived in a burst at the end instead of one per interval */
  buffered: boolean;
  error?: string;
}

export class TransferResult {
  bytes: number;
  ms: number;
  error?: string;
}

/** Everything the browser measured; `long` says whether the slow probes ran. */
export class ClientDiagnostics {
  startedAt: string;
  durationMs: number;
  long: boolean;
  browser: {
    userAgent: string;
    language: string;
    location: string;
    cookiesEnabled: boolean;
    online: boolean;
  };
  echo?: SelftestEcho;
  latency?: {
    samples: TimingSample[];
    medianMs: number;
    maxMs: number;
    error?: string;
  };
  /** server clock minus client clock, corrected by half the round trip */
  clockSkewMs?: number;
  transports?: Record<SelftestTransport, TransportResult>;
  stream?: StreamResult;
  download?: TransferResult;
  upload?: TransferResult;
  resources: ResourceSample[];
}
